import { OnDestroy } from '@angular/core';
import { Timber, AppenderType, EventType, LogLevel, IRemoteConfig } from '@itero/timber';
import { SessionInfo } from 'app/services/authentication/auth.service';
import { BaseDestroyable } from '@core/base-destroyable';
import { AppConfigService } from 'app/services/appConfig/appConfigService';
import { environment } from 'environments/environment';
import { LocalStorageService } from '@core/localStorage.service';
import { BiEvent } from '@logging/interfaces/bi.events';
import { Consts } from '@shared/consts';

export abstract class BaseTimber extends BaseDestroyable implements OnDestroy {

	protected loggerTimber: Timber;
	protected biTimber: Timber;
	loggerTimberInitialized: boolean = false;

	constructor(protected appConfigService: AppConfigService,
		protected localStorageService: LocalStorageService) {
		super();
	}

	protected createLogger(loggedInContactId: number, selectedCompanyId: number): void {
		try {
			const config: IRemoteConfig = {
				appId: 'myitero.com',
				appenderTypes: [AppenderType.Remote, AppenderType.ConsoleNative],
				requiredFields: ['appId', 'module'],
				eventType: EventType.Log,
				url: this.appConfigService.appSettings.loggingEndpoint,
				userId: loggedInContactId,
				companyId: selectedCompanyId,
				minLogLevel: environment.production ? LogLevel[this.appConfigService.appSettings.minLogLevel] : LogLevel.Off
			};
			this.loggerTimber = new Timber(config);
			this.loggerTimberInitialized = true;
			console.log('Logger created with config:', config);
		} catch (e) {
			console.warn('TimberService: Failed to create logger.', e?.message);
		}
	}

	protected createBi(loggedInContactId: number, selectedCompanyId: number): void {
		if (this.appConfigService.isValidConfig('biEndpoint')) {
			const config: IRemoteConfig = {
				appId: 'myitero.com',
				appenderTypes: [AppenderType.BIRemote],
				url: this.appConfigService.appSettings.biEndpoint,
				userId: loggedInContactId,
				companyId: selectedCompanyId,
				minLogLevel: LogLevel.All
			};
			this.biTimber = new Timber(config);
			console.log('BI created with config:', config);
		} else {
			this.biTimber = null;
			console.error('BI object was not created');
		}
	}

	protected sendBiEvent(biEvent: BiEvent) {
		const stringEvent = JSON.stringify(biEvent.event);
		const sessionInfo = this.localStorageService.getItemOrEmpty<SessionInfo>(Consts.Storage.SessionInfo);
		if (sessionInfo && sessionInfo.sessionId) {
			this.biTimber?.biLog(LogLevel.All, {}, {
				extendedParameters: {
					sessionId: sessionInfo.sessionId,
					sessionType: Consts.SessionType,
					component: 'midc',
					type: biEvent.type,
					additionalFields: JSON.parse(stringEvent)
				}
			});
		}
	}

	public setUserConfiguration(contactId: number) {
		if (!this.loggerTimber) { return; }
		this.loggerTimber.setConfig({ userId: contactId, appId: 'myitero.com' });
	}

	public setCompanyConfiguration(companyId: number) {
		if (!this.loggerTimber) { return; }
		this.loggerTimber.setConfig({ companyId: companyId, appId: 'myitero.com' });
	}

	public clearUserConfiguration() {
		if (!this.loggerTimber) { return; }
		this.loggerTimber.setConfig({ userId: undefined, companyId: undefined, appId: 'myitero.com' });
	}
}